import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search } from "lucide-react";
import { ResultPanel } from "@/pages/MasterExclusion";
import { CampaignReferencePanel } from "@/components/CampaignReferencePanel";
import { MultiSelectDropdown } from "@/components/MultiSelectDropdown";

export default function CampaignExclusion() {
  const { options } = useAuth();
  const campaigns = options?.campaigns ?? [];
  const [scope, setScope] = useState([]);
  const [type, setType] = useState("email");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // Per-campaign parameter cache so we don't re-fetch when the agent toggles
  // the same campaign on/off. Keyed by campaign name -> full campaign object.
  const [campaignDetails, setCampaignDetails] = useState({});

  // Fetch the targeting parameters for any newly-selected campaign so the
  // right-hand panel can show them next to the search form. The user
  // explicitly asked: "Once campaign name selected by agent to check
  // campaign exclusion list the campaign parameters should reflect at the
  // right side."
  useEffect(() => {
    const missing = scope.filter((c) => !campaignDetails[c]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const fresh = {};
      for (const name of missing) {
        try {
          const { data } = await api.get(`/campaigns/by-name/${encodeURIComponent(name)}`);
          if (cancelled) return;
          fresh[name] = data;
        } catch (e) {
          // 404/permission errors are non-fatal — we just won't show params
          // for that campaign. Log so devs can spot a recurring problem.
          console.debug("[CampaignExclusion] params fetch failed for", name, e?.response?.status);
        }
      }
      if (!cancelled && Object.keys(fresh).length > 0) {
        setCampaignDetails((prev) => ({ ...prev, ...fresh }));
      }
    })();
    return () => { cancelled = true; };
  }, [scope, campaignDetails]);

  const runCheck = async () => {
    setError(""); setResult(null);
    if (scope.length === 0) return setError("Select at least one campaign in Search Scope.");
    if (!value.trim()) return setError("Enter a value to check.");
    setLoading(true);
    try {
      const { data } = await api.post("/exclusion/campaign", { scope, type, value });
      setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Build the list of campaign objects to render on the right, preserving the
  // order in which the user selected them.
  const selectedCampaignObjects = scope
    .map((name) => campaignDetails[name])
    .filter(Boolean);

  return (
    <div>
      <PageHeader
        eyebrow="Tool · Campaign-scoped"
        title="Campaign Exclusion Check"
        description="Search within selected campaigns only. Useful before launching a new wave."
        testid="campaign-exclusion-header"
      />
      <div className="p-8">
        {/* Two-column layout: form on the left, campaign parameters sticky on the right. */}
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 max-w-3xl space-y-6">
            <div className="border border-border" data-testid="search-scope-panel">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <div className="meta-label">Step 1</div>
                  <h3 className="font-display text-lg font-bold">Search Scope (multi-select)</h3>
                </div>
                <div className="text-xs text-muted-foreground font-mono">{scope.length} selected</div>
              </div>
              <div className="p-6">
                {campaigns.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No campaigns yet. Create one in Admin.</div>
                ) : (
                  <MultiSelectDropdown
                    options={campaigns}
                    value={scope}
                    onChange={setScope}
                    placeholder="Pick one or more campaigns…"
                    testid="scope"
                    maxHeight="280px"
                  />
                )}
              </div>
            </div>

            <div className="border border-border">
              <div className="px-6 py-4 border-b border-border">
                <div className="meta-label">Step 2</div>
                <h3 className="font-display text-lg font-bold">Query</h3>
              </div>
              <div className="p-6 space-y-4">
                <Tabs value={type} onValueChange={(v) => { setType(v); setResult(null); }}>
                  <TabsList className="rounded-sm">
                    <TabsTrigger value="email" data-testid="camp-tab-email" className="rounded-sm">Search by Email</TabsTrigger>
                    <TabsTrigger value="domain" data-testid="camp-tab-domain" className="rounded-sm">Search by Domain</TabsTrigger>
                  </TabsList>
                  <TabsContent value="email" />
                  <TabsContent value="domain" />
                </Tabs>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                    <Input
                      data-testid="campaign-value-input"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder={type === "email" ? "e.g. ceo@target.com" : "e.g. target.com"}
                      className="h-12 rounded-sm pl-10 font-mono"
                      onKeyDown={(e) => e.key === "Enter" && runCheck()}
                    />
                  </div>
                  <Button
                    data-testid="campaign-run-button"
                    onClick={runCheck}
                    disabled={loading}
                    className="h-12 rounded-sm bg-primary hover:bg-[#00227A] px-8"
                  >
                    {loading ? "Checking…" : "Run Check"}
                  </Button>
                </div>
              </div>
            </div>

            {error && <div data-testid="campaign-error" className="badge-error px-4 py-3 rounded-sm text-sm">{error}</div>}
            {result && <ResultPanel result={result} testid="campaign-result" />}
          </div>

          {/* Campaign parameters column — updates as the agent toggles scope. */}
          <aside className="md:w-[340px] lg:w-[380px] xl:w-[420px] md:shrink-0">
            <CampaignReferencePanel campaigns={selectedCampaignObjects} />
          </aside>
        </div>
      </div>
    </div>
  );
}
